package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.*
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.Supplier
import co.uk.sacursoftware.ssinventory.data.model.CreateSupplierRequest
import co.uk.sacursoftware.ssinventory.data.model.EditSupplierRequest
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SupplierScreen(
    initialPageIndex: Int = 0,
    onPageChange: (Int) -> Unit = {},
    onBack: () -> Unit,
    onViewItems: (Supplier) -> Unit
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var suppliers by remember { mutableStateOf<List<Supplier>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    fun refreshSuppliers() {
        scope.launch {
            isLoading = true
            repository.getSuppliers().fold(
                onSuccess = { 
                    suppliers = it
                    isLoading = false
                },
                onFailure = { 
                    error = it.message
                    isLoading = false
                }
            )
        }
    }

    LaunchedEffect(Unit) {
        refreshSuppliers()
    }

    var showAddDialog by remember { mutableStateOf(false) }
    var showEditDialog by remember { mutableStateOf<Supplier?>(null) }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                TopAppBar(
                    title = { ScreenTitle("Suppliers") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        if (userRole == UserRole.ADMIN) {
                            ToolbarPlusButton(onClick = { showAddDialog = true })
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Column(modifier = Modifier.align(Alignment.Center), horizontalAlignment = Alignment.CenterHorizontally) {
                        Text("Error: $error", color = MaterialTheme.colorScheme.error)
                        Button(onClick = { refreshSuppliers() }) { Text("Retry") }
                    }
                } else if (!isLoading && suppliers.isEmpty()) {
                    Text("No suppliers found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    val pagerState = rememberPagerState(
                        initialPage = initialPageIndex.coerceIn(0, maxOf(0, suppliers.size - 1)),
                        pageCount = { suppliers.size }
                    )
                    
                    LaunchedEffect(pagerState.currentPage) {
                        onPageChange(pagerState.currentPage)
                    }
                    
                    Column {
                        ScrollableTabRow(
                            selectedTabIndex = pagerState.currentPage,
                            edgePadding = 16.dp,
                            containerColor = MaterialTheme.colorScheme.surface,
                            contentColor = MaterialTheme.colorScheme.primary,
                            divider = {},
                            indicator = { tabPositions ->
                                if (pagerState.currentPage < tabPositions.size) {
                                    TabRowDefaults.SecondaryIndicator(
                                        modifier = Modifier.tabIndicatorOffset(tabPositions[pagerState.currentPage]),
                                        color = MaterialTheme.colorScheme.secondary
                                    )
                                }
                            }
                        ) {
                            suppliers.forEachIndexed { index, supplier ->
                                Tab(
                                    selected = pagerState.currentPage == index,
                                    onClick = { scope.launch { pagerState.animateScrollToPage(index) } },
                                    text = { Text(supplier.name) }
                                )
                            }
                        }

                        HorizontalPager(
                            state = pagerState,
                            modifier = Modifier.fillMaxSize()
                        ) { page ->
                            SupplierDetailCard(
                                supplier = suppliers[page],
                                onEdit = { showEditDialog = it },
                                onViewItems = { onViewItems(it) }
                            )
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { /* Block touches */ },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }

    if (showAddDialog) {
        SupplierDialog(
            title = "Add Supplier",
            onDismiss = { showAddDialog = false },
            onConfirm = { name, howToOrder, orderAddress, threshold ->
                scope.launch {
                    repository.createSupplier(
                        CreateSupplierRequest(
                            name = name,
                            howToOrder = howToOrder,
                            orderAddress = orderAddress,
                            thresholdOrderPercentage = threshold
                        )
                    ).fold(
                        onSuccess = { response ->
                            if (response.error == null) {
                                showAddDialog = false
                                refreshSuppliers()
                                snackbarHostState.showSnackbar(response.message ?: "Supplier created successfully")
                            } else {
                                snackbarHostState.showSnackbar("Error: ${response.error}")
                            }
                        },
                        onFailure = { 
                            snackbarHostState.showSnackbar("Failed to connect to server: ${it.message}")
                        }
                    )
                }
            }
        )
    }

    showEditDialog?.let { supplier ->
        SupplierDialog(
            title = "Edit Supplier",
            initialSupplier = supplier,
            onDismiss = { showEditDialog = null },
            onConfirm = { name, howToOrder, orderAddress, threshold ->
                scope.launch {
                    repository.editSupplier(
                        EditSupplierRequest(
                            id = supplier.id,
                            name = name,
                            howToOrder = howToOrder,
                            orderAddress = orderAddress,
                            thresholdOrderPercentage = threshold
                        )
                    ).fold(
                        onSuccess = { response ->
                            if (response.error == null) {
                                showEditDialog = null
                                refreshSuppliers()
                                snackbarHostState.showSnackbar(response.message ?: "Supplier updated successfully")
                            } else {
                                snackbarHostState.showSnackbar("Error: ${response.error}")
                            }
                        },
                        onFailure = { 
                            snackbarHostState.showSnackbar("Failed to connect to server: ${it.message}")
                        }
                    )
                }
            }
        )
    }
}

@Composable
fun SupplierDetailCard(
    supplier: Supplier,
    onEdit: (Supplier) -> Unit,
    onViewItems: (Supplier) -> Unit
) {
    Card(
        modifier = Modifier.fillMaxSize().padding(16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = supplier.name, 
                style = MaterialTheme.typography.headlineLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary
            )
            
            HorizontalDivider(
                modifier = Modifier.padding(vertical = 16.dp).width(80.dp),
                thickness = 4.dp,
                color = MaterialTheme.colorScheme.secondary
            )
            
            Spacer(modifier = Modifier.height(16.dp))
            
            Column(modifier = Modifier.fillMaxWidth()) {
                DetailRow("How to Order", supplier.howToOrder ?: "")
                DetailRow("Order Address", supplier.orderAddress ?: "")
                DetailRow("Threshold %", "${supplier.thresholdOrderPercentage ?: 0}%")
            }
            
            Spacer(modifier = Modifier.weight(1f))
            
            val currentUserRole = LocalUserRole.current
            if (currentUserRole == UserRole.ADMIN) {
                Button(
                    onClick = { onEdit(supplier) },
                    modifier = Modifier.fillMaxWidth().height(56.dp),
                    shape = MaterialTheme.shapes.medium
                ) {
                    Text("Edit Supplier Details")
                }
                
                Spacer(modifier = Modifier.height(16.dp))
            }
            
            Button(
                onClick = { onViewItems(supplier) },
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = MaterialTheme.shapes.medium,
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
            ) {
                Text("View Supplier Items")
            }
        }
    }
}

@Composable
fun DetailRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
        verticalAlignment = Alignment.Top
    ) {
        Text(
            text = label, 
            style = MaterialTheme.typography.labelLarge, 
            color = MaterialTheme.colorScheme.secondary,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.width(120.dp)
        )
        Spacer(modifier = Modifier.width(16.dp))
        Text(
            text = value.ifEmpty { "N/A" }, 
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.weight(1f)
        )
    }
}

@Composable
fun SupplierDialog(
    title: String,
    initialSupplier: Supplier? = null,
    onDismiss: () -> Unit,
    onConfirm: (String, String, String, Int) -> Unit
) {
    var name by remember { mutableStateOf(initialSupplier?.name ?: "") }
    var howToOrder by remember { mutableStateOf(initialSupplier?.howToOrder ?: "") }
    var orderAddress by remember { mutableStateOf(initialSupplier?.orderAddress ?: "") }
    var threshold by remember { mutableStateOf(initialSupplier?.thresholdOrderPercentage?.toString() ?: "20") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text("Supplier Name") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = howToOrder,
                    onValueChange = { howToOrder = it },
                    label = { Text("How to Order (e.g. Email, Whatsapp)") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = orderAddress,
                    onValueChange = { orderAddress = it },
                    label = { Text("Order Address / URL") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = threshold,
                    onValueChange = { threshold = it },
                    label = { Text("Threshold Order %") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small
                )
            }
        },
        confirmButton = {
            Button(
                onClick = { 
                    onConfirm(name, howToOrder, orderAddress, threshold.toIntOrNull() ?: 20) 
                }, 
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
